import { AlertCircle, ArrowLeft, Unlink, X } from 'lucide-react';
import { useDeviceDetails } from '../hooks/useDeviceDetails';
import { InterfaceTrafficSection } from './traffic/InterfaceTrafficSection';
import type { TrafficRange } from './traffic/range';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface InterfaceDetailPanelProps {
  deviceId: string;
  interfaceId: string;
  onClose?: () => void;
  onBackToDevice?: () => void;
  /**
   * Whether topology discovered a link on this port.
   *
   * This panel used to be reachable only for ports with no discovered link, so
   * it stated "Not connected" unconditionally. It is now also the "this port
   * alone" surface a connected port drills into — where a LAG member's own
   * story gets read when it differs from the aggregate's — and an unconditional
   * alert would be a false statement there. Undefined means the caller does not
   * know, and the alert is withheld rather than guessed at.
   */
  connected?: boolean;
  /** Range inherited from the page, so the panel and the canvas agree. */
  trafficRange?: TrafficRange;
}

function formatSpeed(speedMbps: number | undefined | null): string {
  if (speedMbps === undefined || speedMbps === null) return 'Unknown';
  if (speedMbps >= 1000) return `${(speedMbps / 1000).toFixed(0)} Gbps`;
  return `${String(speedMbps)} Mbps`;
}

export default function InterfaceDetailPanel({
  deviceId,
  interfaceId,
  onClose,
  onBackToDevice,
  connected,
  trafficRange,
}: InterfaceDetailPanelProps) {
  const { data: device, isLoading, error } = useDeviceDetails({
    deviceId,
    includeInterfaces: true,
  });

  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Loading interface details…</p>
        <Progress value={undefined} className="h-1 mt-2" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Error loading interface:</strong>{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const iface = device?.interfaces?.find((i) => i.id === interfaceId);

  if (!iface) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        <p className="text-sm text-muted-foreground">Interface not found</p>
      </div>
    );
  }

  const isUp = iface.operStatus?.toLowerCase() === 'up';
  const isAdminUp = iface.adminStatus?.toLowerCase() === 'up';

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-4 gap-2">
        <h3 className="text-base font-semibold">Interface details</h3>
        <div className="flex items-center gap-1.5">
          {onBackToDevice ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onBackToDevice}
              className="gap-1.5"
              title="Back to device"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to device
            </Button>
          ) : null}
          {onClose ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      {connected === false ? (
        <Alert className="border-sev-3/40 bg-sev-3/10 mb-4">
          <Unlink className="h-4 w-4 text-sev-3" />
          <AlertDescription>
            <strong>Not connected</strong> — This interface has no discovered link to another device.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="rounded-md border border-border-subtle bg-muted/30 p-3 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
            {iface.interfaceName}
          </Badge>
          <Badge
            className={cn(
              'border-0 font-normal capitalize',
              isUp ? 'bg-sev-4/15 text-sev-4' : 'bg-muted text-muted-foreground',
            )}
          >
            {iface.operStatus || 'Unknown'}
          </Badge>
        </div>
        {iface.description ? (
          <p className="text-xs text-muted-foreground">
            <strong>Description:</strong> {iface.description}
          </p>
        ) : null}
      </div>

      <p className="text-xs text-muted-foreground mb-4">
        <strong>Device:</strong> {device?.name || device?.ipAddress}
      </p>

      <table className="w-full text-sm">
        <tbody className="divide-y divide-border-subtle">
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground py-1.5 w-2/5">
              Type
            </th>
            <td className="py-1.5">{iface.interfaceType || 'Unknown'}</td>
          </tr>
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
              Admin status
            </th>
            <td className="py-1.5">
              <Badge
                className={cn(
                  'border-0 font-normal capitalize',
                  isAdminUp ? 'bg-sev-4/15 text-sev-4' : 'bg-muted text-muted-foreground',
                )}
              >
                {iface.adminStatus || 'Unknown'}
              </Badge>
            </td>
          </tr>
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
              Oper status
            </th>
            <td className="py-1.5">
              <Badge
                className={cn(
                  'border-0 font-normal capitalize',
                  isUp ? 'bg-sev-4/15 text-sev-4' : 'bg-muted text-muted-foreground',
                )}
              >
                {iface.operStatus || 'Unknown'}
              </Badge>
            </td>
          </tr>
          {iface.speedMbps ? (
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                Speed
              </th>
              <td className="py-1.5">{formatSpeed(iface.speedMbps)}</td>
            </tr>
          ) : null}
          {iface.hardwareMaxSpeedMbps ? (
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                Max speed (HW)
              </th>
              <td className="py-1.5">{formatSpeed(iface.hardwareMaxSpeedMbps)}</td>
            </tr>
          ) : null}
          {iface.macAddress ? (
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                MAC address
              </th>
              <td className="py-1.5">
                <code className="font-mono text-xs">{iface.macAddress}</code>
              </td>
            </tr>
          ) : null}
          {iface.ifindex ? (
            <tr>
              <th className="text-left text-xs font-medium text-muted-foreground py-1.5">
                Index
              </th>
              <td className="py-1.5">{iface.ifindex}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      {iface.parentRelationships && iface.parentRelationships.length > 0 ? (
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-2">LAG membership</h4>
          <div className="flex flex-wrap gap-1">
            {iface.parentRelationships.map((rel) => (
              <Badge
                key={rel.interfaceId}
                className="bg-sev-3/15 text-foreground border-0 font-normal"
              >
                {rel.interfaceName} ({rel.relationshipType})
              </Badge>
            ))}
          </div>
        </div>
      ) : null}

      {iface.childRelationships && iface.childRelationships.length > 0 ? (
        <div className="mt-4">
          <h4 className="text-sm font-semibold mb-2">
            LAG members ({iface.childRelationships.length})
          </h4>
          <div className="flex flex-wrap gap-1">
            {iface.childRelationships.map((rel) => (
              <Badge
                key={rel.interfaceId}
                variant="secondary"
                className="font-normal"
              >
                {rel.interfaceName}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}

      <InterfaceTrafficSection
        deviceId={deviceId}
        interfaceId={iface.id}
        interfaceName={iface.interfaceName}
        interfaceType={iface.interfaceType}
        range={trafficRange}
      />
    </div>
  );
}
